import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  AccessibilityHelp,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlComment,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SimpleUploadAdapter,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  Undo
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminEditArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // showPreview removed - CKEditor has built-in preview functionality

  useEffect(() => {
    // Check auth
    if (!sessionStorage.getItem('adminAuth')) {
      navigate('/admin');
      return;
    }
    
    // Fetch article data
    const loadArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
        setEditData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching article:', error);
        setLoading(false);
      }
    };
    
    loadArticle();
  }, [slug, navigate]);

  const handleSave = async () => {
    setSaving(true);
    console.log('🔍 Attempting to save article:', {
      article_id: article.id,
      endpoint: `${API}/articles/${article.id}`,
      data: {
        title: editData.title,
        excerpt: editData.excerpt,
        content: editData.content,
        category: editData.category,
        image_url: editData.image_url
      }
    });
    
    try {
      const response = await axios.put(`${API}/articles/${article.id}`, {
        title: editData.title,
        excerpt: editData.excerpt,
        content: editData.content,
        category: editData.category,
        image_url: editData.image_url
      });
      
      console.log('✅ Save successful:', response.data);
      alert('✅ Article mis à jour avec succès !');
      navigate('/admin/articles');
    } catch (error) {
      console.error('❌ Error updating article:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      alert(`❌ Erreur lors de la mise à jour: ${error.response?.data?.detail || error.message}`);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/articles');
  };

  // Functions removed - now using CKEditor instead of custom toolbar

  if (loading) {
    return (
      <div className="app">
        <div className="loading-container">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="app">
        <div className="error-container">
          <h2>Article non trouvé</h2>
          <button onClick={() => navigate('/admin/articles')} className="btn btn-primary">
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-text">AmsWeb Admin</span>
          </div>
          <div className="nav-links">
            <button onClick={() => navigate('/admin/articles')} className="btn btn-secondary">
              ← Dashboard
            </button>
          </div>
        </div>
      </nav>

      <section className="admin-edit-section">
        <div className="container">
          <h1>✏️ Éditer l&apos;Article</h1>
          
          <div className="admin-edit-form">
            <div className="form-group">
              <label>Titre :</label>
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData({...editData, title: e.target.value})}
                className="admin-form-input"
              />
            </div>

            <div className="form-group">
              <label>Slug (URL) :</label>
              <input
                type="text"
                value={editData.slug}
                disabled
                className="admin-form-input"
                style={{background: '#f3f4f6', cursor: 'not-allowed'}}
              />
              <small>Le slug ne peut pas être modifié</small>
            </div>

            <div className="form-group">
              <label>Catégorie :</label>
              <select
                value={editData.category}
                onChange={(e) => setEditData({...editData, category: e.target.value})}
                className="admin-form-select"
              >
                <option>Le Marketing de réseau et le NetWorking</option>
                <option>Les outils pour bien démarrer</option>
                <option>Découvrir les nouveaux MLM</option>
              </select>
            </div>

            <div className="form-group">
              <label>Image URL :</label>
              <input
                type="text"
                value={editData.image_url}
                onChange={(e) => setEditData({...editData, image_url: e.target.value})}
                className="admin-form-input"
              />
              {editData.image_url && (
                <div className="image-preview">
                  <img src={editData.image_url} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Extrait :</label>
              <textarea
                value={editData.excerpt}
                onChange={(e) => setEditData({...editData, excerpt: e.target.value})}
                className="admin-form-textarea"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Contenu Complet :</label>
              
              <div style={{ marginTop: '1rem' }}>
                <CKEditor
                  editor={ClassicEditor}
                  data={editData.content || ''}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setEditData({...editData, content: data});
                  }}
                  config={{
                    licenseKey: 'GPL',
                    toolbar: {
                      items: [
                        'undo', 'redo',
                        '|',
                        'sourceEditing', 'showBlocks',
                        '|',
                        'heading',
                        '|',
                        'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor',
                        '|',
                        'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', 'code', 'removeFormat',
                        '|',
                        'link', 'insertImage', 'mediaEmbed', 'insertTable', 'highlight', 'blockQuote', 'codeBlock', 'htmlEmbed',
                        '|',
                        'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent'
                      ],
                      shouldNotGroupWhenFull: false
                    },
                    plugins: [
                      AccessibilityHelp, Autoformat, AutoImage, AutoLink, Autosave, BalloonToolbar, 
                      BlockQuote, Bold, Code, CodeBlock, Essentials, FindAndReplace, FontBackgroundColor, 
                      FontColor, FontFamily, FontSize, FullPage, GeneralHtmlSupport, Heading, Highlight, 
                      HorizontalLine, HtmlComment, HtmlEmbed, ImageBlock, ImageCaption, ImageInline, 
                      ImageInsert, ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, 
                      ImageToolbar, ImageUpload, Indent, IndentBlock, Italic, Link, LinkImage, List, 
                      ListProperties, MediaEmbed, Paragraph, PasteFromOffice, RemoveFormat, SelectAll, 
                      ShowBlocks, SourceEditing, SpecialCharacters, SpecialCharactersArrows, 
                      SpecialCharactersCurrency, SpecialCharactersEssentials, SpecialCharactersLatin, 
                      SpecialCharactersMathematical, SpecialCharactersText, Strikethrough, Style, 
                      Subscript, Superscript, Table, TableCaption, TableCellProperties, TableColumnResize, 
                      TableProperties, TableToolbar, TextTransformation, TodoList, Underline, Undo
                    ],
                    balloonToolbar: ['bold', 'italic', '|', 'link', '|', 'bulletedList', 'numberedList'],
                    fontFamily: {
                      supportAllValues: true
                    },
                    fontSize: {
                      options: [10, 12, 14, 'default', 18, 20, 22, 24, 26, 28, 36, 48, 72],
                      supportAllValues: true
                    },
                    heading: {
                      options: [
                        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                        { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
                      ]
                    },
                    htmlSupport: {
                      allow: [
                        {
                          name: /^.*$/,
                          styles: true,
                          attributes: true,
                          classes: true
                        }
                      ]
                    },
                    image: {
                      toolbar: [
                        'toggleImageCaption', 'imageTextAlternative', '|',
                        'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|',
                        'resizeImage'
                      ]
                    },
                    link: {
                      addTargetToExternalLinks: true,
                      defaultProtocol: 'https://',
                      decorators: {
                        toggleDownloadable: {
                          mode: 'manual',
                          label: 'Downloadable',
                          attributes: {
                            download: 'file'
                          }
                        }
                      }
                    },
                    list: {
                      properties: {
                        styles: true,
                        startIndex: true,
                        reversed: true
                      }
                    },
                    menuBar: {
                      isVisible: true
                    },
                    placeholder: 'Commencez à écrire ou collez votre contenu ici...',
                    table: {
                      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
                    }
                  }}
                />
              </div>
            </div>

            <div className="admin-edit-buttons">
              <button 
                onClick={handleSave} 
                className="btn btn-primary btn-large"
                disabled={saving}
              >
                {saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
              </button>
              <button 
                onClick={handleCancel} 
                className="btn btn-secondary btn-large"
                disabled={saving}
              >
                ❌ Annuler
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AdminEditArticle;
